import React, {
  Fragment,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  Children,
} from 'react'
import styled, { keyframes } from 'styled-components'

import type { ReactNode, RefAttributes } from 'react'

type MarqueeStaticProps = {
  autoFill?: boolean
  speed?: number
  delay?: number
  loop?: number
  onFinish?: () => void
  onCycleComplete?: () => void
  onMount?: () => void
  paused?: boolean
  children?: ReactNode
} & RefAttributes<HTMLDivElement>

/**
 * Clone of https://github.com/justin-chu/react-fast-marquee
 */
const ITEM_WIDTH = 150

export const MarqueeStatic = React.forwardRef(
  (
    {
      autoFill = true,
      speed = 50,
      loop = 0,
      onFinish,
      onCycleComplete,
      onMount,
      children,
    }: MarqueeStaticProps,
    forwardedRef: React.ComponentPropsWithRef<'div'>['ref'],
  ) => {
    const [containerWidth, setContainerWidth] = useState(0)
    const [marqueeWidth, setMarqueeWidth] = useState(0)
    const [multiplier, setMultiplier] = useState(1)
    const [isMounted, setIsMounted] = useState(false)
    const marqueeRef = useRef<HTMLDivElement>(null)

    const [animationState, setAnimationState] = useState<'running' | 'paused'>(
      'paused',
    )

    const calculateWidth = useCallback(() => {
      if (marqueeRef.current) {
        const containerRect = marqueeRef.current.getBoundingClientRect()
        const marqueeRect = marqueeRef.current.getBoundingClientRect()
        const containerWidth = containerRect.width
        const marqueeWidth = marqueeRect.width

        if (autoFill && containerWidth && marqueeWidth) {
          setMultiplier(
            marqueeWidth < containerWidth
              ? Math.ceil(containerWidth / marqueeWidth)
              : 1,
          )
        } else {
          setMultiplier(1)
        }

        setContainerWidth(containerWidth)
        setMarqueeWidth(marqueeWidth)
      }
    }, [autoFill, marqueeRef])

    const toggleMarqueeAnimation = useCallback(() => {
      if (marqueeRef.current) {
        const parentNodeWidth =
          marqueeRef.current.parentElement?.parentElement?.getBoundingClientRect()
            .width

        if (parentNodeWidth) {
          const marqueeWidth = Children.count(children) * ITEM_WIDTH

          if (marqueeWidth > parentNodeWidth) {
            setAnimationState('running')
          }
        }
      }
    }, [marqueeRef, children])

    useEffect(() => {
      if (!isMounted) {
        return
      }

      calculateWidth()
      toggleMarqueeAnimation()

      if (marqueeRef.current) {
        const resizeObserver = new ResizeObserver(() => {
          calculateWidth()
          toggleMarqueeAnimation()
        })
        resizeObserver.observe(marqueeRef.current)
        return () => {
          resizeObserver.disconnect()
        }
      }
    }, [calculateWidth, isMounted, children, toggleMarqueeAnimation])

    useEffect(calculateWidth, [calculateWidth, children])

    useEffect(() => setIsMounted(true), [])

    useEffect(() => {
      if (typeof onMount === 'function') {
        onMount()
      }
    }, [onMount])

    const duration = useMemo(() => {
      if (autoFill) {
        return (marqueeWidth * multiplier) / speed
      } else {
        return marqueeWidth < containerWidth
          ? containerWidth / speed
          : marqueeWidth / speed
      }
    }, [autoFill, containerWidth, marqueeWidth, multiplier, speed])

    const marqueeStyle = useMemo(
      () => ({
        ['--animation-state' as string]: animationState,
        ['--duration' as string]: `${duration}s`,
        ['--iteration-count' as string]: loop ? `${loop}` : 'infinite',
        ['--min-width' as string]: autoFill ? `auto` : '100%',
      }),
      [animationState, duration, loop, autoFill],
    )

    const multiplyChildren = useCallback(
      (multiplier: number) => {
        return [
          ...Array.from({
            length:
              Number.isFinite(multiplier) && multiplier >= 0 ? multiplier : 0,
          }),
        ].map((_, i) => (
          <Fragment key={i}>
            {Children.map(children, (child) => (
              <div>{child}</div>
            ))}
          </Fragment>
        ))
      },
      [children],
    )

    return (
      <Root ref={forwardedRef}>
        <Lane
          style={marqueeStyle}
          onAnimationIteration={onCycleComplete}
          onAnimationEnd={onFinish}
        >
          <InitialChild ref={marqueeRef}>
            {Children.map(children, (child) => (
              <Child>{child}</Child>
            ))}
          </InitialChild>
          {multiplyChildren(multiplier - 1)}
        </Lane>
        {animationState === 'running' ? (
          <Lane style={marqueeStyle}>{multiplyChildren(multiplier)}</Lane>
        ) : undefined}
      </Root>
    )
  },
)

const Root = styled.div`
  overflow-x: hidden;
  display: flex;
  flex-direction: row;
  position: relative;
  width: 100%;

  &:hover > div {
    animation-play-state: paused;
  }
`

const scrollAnimation = keyframes`
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-100%);
  }
`

const Lane = styled.div`
  flex: 0 0 auto;
  min-width: var(--min-width);
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;

  animation: ${scrollAnimation} var(--duration) linear 0s infinite;
  animation-play-state: var(--animation-state);
  animation-delay: 0s;
  animation-direction: normal;

  // prevent vertical scrolling on Windows
  overflow-y: hidden;
`

const InitialChild = styled.div`
  flex: 0 0 auto;
  display: flex;
  min-width: auto;
  flex-direction: row;
  align-items: center;
`

const Child = styled.div``
